import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import Toggle from 'react-toggle';
import styled from 'styled-components/macro';
import cx from 'classnames';

import DownloadDropdown, { Download } from '../../components/dropdowns/DownloadDropdown/DownloadDropdown';
import { FULL_MODE, PPS_PURCHASED } from 'src/client/helpers/constants';
import { toggleLights } from '../../redux/modules/movie';
import { useSelector } from '../../redux/modules/helpers/useSelector';
import { TABLET_PORTRAIT } from '@tovia/man-ui';

type Props = {
  accessMode: string | null;
  accessWithToken: boolean;
  contentCdnUrl: string;
  isLightsOn: boolean;
  setShowUpgradeModal: (value: boolean) => void;
};

const MovieActions = (props: Props) => {
  const { accessMode, accessWithToken, contentCdnUrl, isLightsOn, setShowUpgradeModal } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const { item } = useSelector((state) => state.movie);
  const { allowedStreamIds, downloadCounts } = useSelector((state) => state.galleryPermissions);

  if (!(accessMode === FULL_MODE || accessMode === PPS_PURCHASED)) return null;

  const lightsLabel = isLightsOn
    ? intl.formatMessage({ id: 'movie.buttons.lowerLights', defaultMessage: 'Lower Lights:' })
    : intl.formatMessage({ id: 'movie.buttons.raiseLights', defaultMessage: 'Raise Lights:' });

  const movieDownloads: Download[] = (item?.files?.sizes?.videos || [])
    .filter((video) => allowedStreamIds.includes(video.id))
    .map((video) => ({
      id: video.id,
      size: video.size,
      link: `/api/download-media/${item?.UUID}/film/${video.id}`,
      title: downloadTitles[video.id],
      isDownloadMedia: true,
    }));

  const zipDownloads: Download[] = (item?.files?.sizes?.zips || []).map((zip) => ({
    ...zip,
    id: zip.quality,
    link: `/api/download-media/${item?.UUID}/photos/${zip.quality}`,
    title: downloadTitles[zip.quality],
    isDownloadMedia: true,
  }));

  const relatedPhotos: Download[] = (item?.files?.sizes?.relatedPhotos || []).map((photo) => {
    const related = GALLERY_RELATED_PHOTOS.find(({ id }) => id === photo.id);
    return {
      id: photo.id,
      link: relatedToUrl(item, related, contentCdnUrl),
      title: downloadTitles[photo.id],
    };
  });

  const hasDownloads = movieDownloads.length > 0 || zipDownloads.length > 0 || relatedPhotos.length > 0;

  const handleTrialClick = (link) => {
    if (downloadCounts.allowedToDownload) {
      window.location.href = link;
    } else {
      setShowUpgradeModal(true);
    }
  };

  return (
    <UserActions className="col-md-6 col-xs-12 no-pad">
      {!accessWithToken && !item?.downloadsDisabled && (
        <Downloads>
          {hasDownloads && <FormattedMessage id="movie.download" defaultMessage="Download:" />}
          <DownloadDropdown
            key="download-film"
            label={intl.formatMessage({ id: 'movie.buttons.film', defaultMessage: 'Film' })}
            downloads={movieDownloads}
            onItemClick={handleTrialClick}
            iconType="movie"
          />
          <DownloadDropdown
            key="download-photos"
            label={intl.formatMessage({ id: 'movie.buttons.photos', defaultMessage: 'Photos' })}
            downloads={[...zipDownloads, ...relatedPhotos]}
            onItemClick={handleTrialClick}
            iconType="photo"
          />
        </Downloads>
      )}
      {item?.downloadsDisabled && <DownloadsDisabled>Online streaming only.</DownloadsDisabled>}
      <LightsBox key="user-lower-lights" className={cx('pull-right hidden-xs', { dimmed: !isLightsOn }, 'dimming')}>
        <LightsText className="va-m inline-block">{lightsLabel}</LightsText>
        <Toggle
          className="va-m inline-block"
          checked={!isLightsOn}
          icons={false}
          id="light-switch"
          onChange={() => dispatch(toggleLights())}
        />
      </LightsBox>
    </UserActions>
  );
};

const DownloadsDisabled = styled.span`
  margin-right: 20px;
`;

const UserActions = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 10px;
  border: 1px solid ${(props) => props.theme.primary3};

  @media ${TABLET_PORTRAIT} {
    justify-content: flex-end;
    border: 0;
    padding: 0;
  }
`;

const Downloads = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const LightsBox = styled.div`
  display: flex;
  order: 2;
  align-items: center;

  .react-toggle--checked .react-toggle-track {
    background: rgb(156, 156, 156);
  }

  .react-toggle--checked:hover:not(.react-toggle--disabled) .react-toggle-track {
    background: rgb(156, 156, 156);
  }

  .react-toggle--checked .react-toggle-thumb {
    border-color: rgb(156, 156, 156);
  }

  .react-toggle:active:not(.react-toggle--disabled) .react-toggle-thumb,
  .react-toggle--focus .react-toggle-thumb {
    box-shadow: 0 0 2px #fff;
  }
`;

const LightsText = styled.span`
  padding-right: 10px;
`;

const downloadTitles = {
  // movies
  '4k': '4k UHD',
  '864p': '864p UHD',
  '1080p': '1080p HD',
  '720p': '720p HD',
  '480p': '480p SD',
  '360p': '360p SD',
  '270p': '270p SD',
  '240p': '240p SD',
  wmv: 'WMV',
  avi: 'DivX AVI',

  // photos
  cover: 'Cover',
  clean: 'Clean Cover',
  wide: 'Wide Cover',
  'wide-clean': 'Wide Clean Cover',
  high: 'High Zip',
  med: 'Medium Zip',
  low: 'Low Zip',
};

const PHOTOS_EXT = 'jpg';

const GALLERY_RELATED_PHOTOS = [
  { id: 'clean', fileName: 'clean', zippedFileName: 'clean-cover', ext: PHOTOS_EXT },
  { id: 'cover', fileName: 'cover', ext: PHOTOS_EXT },
  { id: 'wide', fileName: 'wide', ext: PHOTOS_EXT },
  { id: 'wide-clean', fileName: 'wide_clean', ext: PHOTOS_EXT },
];

const relatedToUrl = (item, related, contentCdnUrl) => {
  const path = [contentCdnUrl, item.siteUUID, 'media', item.UUID].join('/');
  const file = `${related.fileName}_${item.UUID}.${related.ext}`;
  return `${path}/${file}?download=1&filename=${related.fileName}.${related.ext}`;
};

export default MovieActions;
